// pages/AuthCallback.js
//
// Where Google / Apple sign-in lands (services/socialAuth.js). The Supabase
// client reads the session from the URL itself (detectSessionInUrl); this
// page waits for it, makes sure the Invits profile row exists (auth-codes
// oauth_complete - name, photo and verified email from the provider), and
// sends the person on: to profile setup the first time, otherwise back to
// where they started (an invitation they were RSVPing to, say) or home.
import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import supabase from '../services/supabase';
import { completeOAuthSignIn } from '../services/authCodes';
import '../styles/Auth.scss';

/** Only same-site paths: `next` comes from the URL, so it must not send anyone off-site. */
const safeNext = (value) => (value && value.startsWith('/') && !value.startsWith('//') ? value : null);

async function waitForSession(timeoutMs = 8000) {
  const { data } = await supabase.auth.getSession();
  if (data?.session) return data.session;
  return new Promise((resolve) => {
    const timer = setTimeout(() => { sub.unsubscribe(); resolve(null); }, timeoutMs);
    const { data: { subscription: sub } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) {
        clearTimeout(timer);
        sub.unsubscribe();
        resolve(session);
      }
    });
  });
}

export default function AuthCallback() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const providerError = params.get('error_description') || params.get('error');
      if (providerError) { setError(providerError); return; }

      const session = await waitForSession();
      if (cancelled) return;
      if (!session) { setError('Sign-in was not completed. Please try again.'); return; }

      try {
        const result = await completeOAuthSignIn();
        if (cancelled) return;
        const next = safeNext(params.get('next'));
        if (!result?.profile_complete) {
          navigate('/profile-setup', { replace: true, state: next ? { redirectTo: next } : undefined });
        } else {
          navigate(next || '/home', { replace: true });
        }
      } catch (err) {
        if (!cancelled) setError(err.message || 'Something went wrong. Please try again.');
      }
    })();
    return () => { cancelled = true; };
  }, [navigate, params]);

  return (
    <div className="auth-container">
      <div className="auth-content" style={{ textAlign: 'center' }}>
        {error ? (
          <div className="auth-card">
            <h1 className="auth-title">Sign-in failed</h1>
            <p className="auth-subtitle">{error}</p>
            <Link to="/login" className="btn btn-primary">Back to sign in</Link>
          </div>
        ) : (
          <>
            <div className="spinner" />
            <p className="auth-subtitle">Signing you in…</p>
          </>
        )}
      </div>
    </div>
  );
}
